import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import LockWrap from "../components/LockWrap";
import ProtectedRoute from "../components/ProtectedRoute";
import "../css/Labs.css";
import { apiService, type CourseDetailResponse, type LabProgressResponse } from "../services/api";
import { useAuth } from '../hooks/useAuth'
import LabModal from "../components/LabModal";

export const Route = createFileRoute("/courses/$id/labs")({
  component: CourseLabs,
});

function CourseLabs() {
  const { id } = Route.useParams();
  const [courseData, setCourseData] = useState<CourseDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [labProgress, setLabProgress] = useState<Record<string, LabProgressResponse>>({});
  const [activeLabId, setActiveLabId] = useState<string | null>(null);
  const { isAuthenticated, isPremium } = useAuth();

  // Clear active lab ID when navigating away
  useEffect(() => {
    const handleBeforeUnload = () => {
      setActiveLabId(null);
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  useEffect(() => {
    const loadCourse = async () => {
      try {
        setLoading(true);
        const data = await apiService.getCourseById(id);
        setCourseData(data);

// Fetch progress for all labs
         const progressPromises = data.course.labs?.map(async (lab) => {
           try {
             const progress = await apiService.getLabProgress(Number(lab.id).toString());
             return { id: lab.id, progress };
            } catch {
             return { id: lab.id, progress: null };
           }
         }) || [];

         const progressResults = await Promise.all(progressPromises);
         const progressMap: Record<string, LabProgressResponse> = {};
         progressResults.forEach(({ id, progress }) => {
           if (progress) progressMap[id] = progress;
         });
         setLabProgress(progressMap);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load course');
      } finally {
        setLoading(false);
      }
    };
    
    loadCourse();
  }, [id]);

  if (loading) {
    return (
      <ProtectedRoute>
        <div style={{ padding: 40, background: "#0a0f1a", color: "#f5f5f5", minHeight: "100vh" }}>
          <div style={{ textAlign: 'center', padding: 40 }}>
            <div style={{ fontSize: '1.2rem', opacity: 0.8 }}>Loading labs...</div>
          </div>
        </div>
      </ProtectedRoute>
    );
  }

  if (error || !courseData?.course) {
    return (
      <ProtectedRoute>
        <div style={{ padding: 40, background: "#0a0f1a", color: "#f5f5f5", minHeight: "100vh" }}>
          <div style={{
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.2)',
            color: '#ef4444',
            padding: 20,
            borderRadius: 8,
            marginBottom: 20
          }}>
            <h2>{error || 'Course not found'}</h2>
            <Link to="/courses" style={{ color: '#6b7280' }}>← Back to courses</Link>
          </div>
        </div>
      </ProtectedRoute>
    );
  }

  const course = courseData.course;
  const labs = course.labs ?? [];

  return (
    <ProtectedRoute>
      <div style={{ padding: 40, background: "#0a0f1a", color: "#f5f5f5", minHeight: "100vh" }}>
      {/* Breadcrumbs */}
      <div style={{ marginBottom: 32, display: "flex", gap: 8, alignItems: "center", fontSize: "0.9rem", opacity: 0.8 }}>
        <Link to="/" style={{ color: "#6b7280", textDecoration: "none" }}>Home</Link>
        <span>{'>'}</span>
        <Link to="/courses" style={{ color: "#6b7280", textDecoration: "none" }}>Courses</Link>
        <span>{'>'}</span>
        <Link to={`/courses/${course.slug}`} style={{ color: "#6b7280", textDecoration: "none" }}>{course.title}</Link>
        <span>{'>'}</span>
        <span style={{ color: "#4ade80" }}>Labs</span>
      </div>
      
      {/* Main Navigation */}
      <div style={{ marginBottom: 32 }}>
        <Link className="link-button" to={`/courses/${course.slug}`}>
          ← Back to Course
        </Link>
      </div>
      
      {/* Header */}
      <div style={{ marginBottom: 40 }}>
        <h1 style={{ fontSize: "2.5rem", marginBottom: 16 }}>{course.title} — Labs</h1>
        <p style={{ fontSize: "1.1rem", opacity: 0.9 }}>Hands-on exercises to practice what you've learned</p>
      </div>
      <div className="lab-grid">
        {labs.length > 0 ? labs.map((lab) => (
          <div key={lab.id} style={{ position: "relative" }}>
            {labProgress[lab.id]?.completed && (
              <div style={{
                position: "absolute",
                top: "-12px",
                right: "12px",
                background: "linear-gradient(135deg, #4ade80, #22d3ee)",
                color: "#0a0f1a",
                padding: "4px 12px",
                borderRadius: "12px",
                fontSize: "0.75rem",
                fontWeight: "700",
                boxShadow: "0 4px 12px rgba(74, 222, 128, 0.3)",
                zIndex: 10,
              }}>
                Completed
              </div>
            )}
            <div className="lab-card">
              <LockWrap isLocked={isPremium ? false : lab.isPremium}>
                <div className="lab-card-inner">
                <div style={{ marginBottom: 16 }}>
                  <h3 style={{ color: "#22d3ee", marginBottom: 8 }}>{lab.title}</h3>
                  <p style={{ opacity: 0.8, fontSize: "0.9rem" }}>
                    {lab.difficulty} • {lab.steps?.length || 0} steps
                  </p>
                </div>
                
                {isAuthenticated && (isPremium || !lab.isPremium) ? (
<button
                     type="button"
                     onClick={() => setActiveLabId(lab.id)}
                     style={{
                       padding: "8px 16px",
                       background: labProgress[lab.id]?.completed ? "#4ade80" : "#22d3ee",
                       color: "#0a0f1a",
                       textDecoration: "none",
                       borderRadius: 6,
                       fontSize: "0.9rem",
                       fontWeight: 500,
                       display: "inline-block",
                       cursor: "pointer",
                       border: "none"
                     }}
                   >
                     {labProgress[lab.id]?.completed ? "Do it again →" : "Start Lab →"}
                   </button>
                ) : (
                  <span style={{
                    padding: "8px 16px",
                    background: "rgba(255, 165, 0, 0.1)",
                    color: "#ffa500",
                    borderRadius: 6,
                    fontSize: "0.9rem",
                    border: "1px solid rgba(255, 165, 0, 0.2)",
                    display: "inline-block"
                  }}>
                    🔒 Locked
                  </span>
                )}
              </div>
            </LockWrap>
          </div>
        </div>
        )) : (
          <div style={{ 
            textAlign: "center", 
            padding: 60, 
            color: "#6b7280",
            border: "1px dashed #374151",
            borderRadius: 12
          }}>
            <h3>No labs available yet</h3>
            <p>Labs for this course are coming soon!</p>
          </div>
        )}
      </div>
      
      {/* Footer Navigation */}
      <div style={{ marginTop: 60, textAlign: "center", borderTop: "1px solid #374151", paddingTop: 40 }}>
        <div style={{ display: "flex", gap: 16, justifyContent: "center", flexWrap: "wrap" }}>
          <Link to={`/courses/${course.id}`} style={{
            padding: "12px 24px",
            background: "#22d3ee",
            color: "#0a0f1a",
            textDecoration: "none",
            borderRadius: 8,
            fontSize: "1rem",
            fontWeight: 500
          }}>
            Back to Course
          </Link>
          <Link to="/labs" style={{
            padding: "12px 24px",
            background: "rgba(74, 222, 128, 0.1)",
            color: "#4ade80",
            textDecoration: "none",
            borderRadius: 8,
            border: "1px solid rgba(74, 222, 128, 0.2)",
            fontSize: "1rem"
          }}>
            All Labs
          </Link>
        </div>
      </div>
      </div>
{activeLabId && (
         <LabModal 
           labId={activeLabId} 
           onClose={() => setActiveLabId(null)} 
           isRetry={labProgress[activeLabId]?.completed || false}
         />
       )}
    </ProtectedRoute>
  );
}
